import { Document, Image, Text, View } from '@react-pdf/renderer'
import type { NiiFormSectionBlock } from 'features/libra/stepComponents/niiReport/content/buildNiiFormSectionBlocks'
import { buildOutcomeOverrideRows } from 'features/libra/stepComponents/niiReport/content/buildNiiReportContent'
import {
  buildNiiReviewMilestoneTable,
  buildNiiSignoffTable,
  NII_APPROVED_BEFORE_SIGNOFF_NOTE,
} from 'features/libra/stepComponents/niiReport/content/buildNiiReviewContent'
import {
  buildZoneOverridesTable,
  type NiiZoneDetail,
} from 'features/libra/stepComponents/niiReport/content/buildNiiZoneAnalysisContent'
import type {
  NiiQuestionList,
  NiiReportMeta,
  NiiReportSection,
  NiiZoneInventoryEntry,
} from 'features/libra/stepComponents/niiReport/content/niiReportContentTypes'
import { NII_REPORT_VALIDITY_NOTICE } from 'features/libra/stepComponents/niiReport/niiReportValidity'
import {
  ELIGIBILITY_LABELS,
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { PdfFieldRow } from 'features/reporting/pdfGeneration/components/shared/PdfFieldRow'
import { PdfFieldRows } from 'features/reporting/pdfGeneration/components/shared/PdfFieldRows'
import { PdfSection } from 'features/reporting/pdfGeneration/components/shared/PdfSection'
import {
  PDF_HEADING_MIN_PRESENCE,
  PdfStyles,
} from 'features/reporting/pdfGeneration/components/shared/pdfStyles'
import { PdfTable } from 'features/reporting/pdfGeneration/components/shared/PdfTable'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import {
  sanitizeForPdf,
  stripLeadingBullet,
} from 'features/reporting/pdfGeneration/utils/formatters'
import React from 'react'

import { niiSectionFitsOnOnePage } from './estimateNiiSectionHeight'
import { ELIGIBILITY_PDF_COLORS, NiiPdfStyles } from './NiiReportPdfStyles'

export interface NiiReportPdfProps {
  chrome: NiiReportMeta
  steps: ReadonlyArray<NiiReportSection>
}

const SubsectionTitle: React.FC<{ children: string }> = ({ children }) => (
  <Text
    style={NiiPdfStyles.subsectionTitle}
    minPresenceAhead={PDF_HEADING_MIN_PRESENCE}>
    {sanitizeForPdf(children)}
  </Text>
)

const ZoneHeading: React.FC<{
  color: string
  children: React.ReactNode
}> = ({ color, children }) => (
  <View
    style={NiiPdfStyles.zoneHeading}
    wrap={false}
    minPresenceAhead={PDF_HEADING_MIN_PRESENCE}>
    <View style={[NiiPdfStyles.zoneColorDot, { backgroundColor: color }]} />
    <Text style={NiiPdfStyles.zoneTitle}>{children}</Text>
  </View>
)

const EligibilityTag: React.FC<{ status: EligibilityStatus }> = ({
  status,
}) => {
  const bgColor =
    ELIGIBILITY_PDF_COLORS[status] ?? ELIGIBILITY_PDF_COLORS.unclear
  return (
    <View style={[NiiPdfStyles.eligibilityTag, { backgroundColor: bgColor }]}>
      <Text style={NiiPdfStyles.eligibilityTagText}>
        {ELIGIBILITY_LABELS[status] ?? 'Unknown'}
      </Text>
    </View>
  )
}

const RecommendationsList: React.FC<{
  items: ReadonlyArray<string>
}> = ({ items }) => (
  <View>
    {items.map((item, i) => (
      <View key={i} style={NiiPdfStyles.recommendationItem} wrap={false}>
        <Text style={NiiPdfStyles.recommendationBullet}>•</Text>
        <Text style={NiiPdfStyles.recommendationText}>
          {sanitizeForPdf(stripLeadingBullet(item))}
        </Text>
      </View>
    ))}
  </View>
)

const QuestionLists: React.FC<{ lists: ReadonlyArray<NiiQuestionList> }> = ({
  lists,
}) => (
  <View>
    {lists.map((list, listIndex) => (
      <View
        key={listIndex}
        style={listIndex > 0 ? NiiPdfStyles.blockGap : undefined}>
        {list.title && <SubsectionTitle>{list.title}</SubsectionTitle>}
        {list.override &&
          buildOutcomeOverrideRows(list.override).map(({ label, value }) => (
            <PdfFieldRow key={label} label={label}>
              <Text style={PdfStyles.preformattedText}>
                {sanitizeForPdf(value)}
              </Text>
            </PdfFieldRow>
          ))}
        {list.answers.map(({ question, answer }, index) => (
          <View
            key={index}
            style={NiiPdfStyles.preScreeningAnswerRow}
            wrap={false}>
            <View
              style={[
                NiiPdfStyles.preScreeningAnswerTag,
                answer
                  ? NiiPdfStyles.preScreeningYesTag
                  : NiiPdfStyles.preScreeningNoTag,
              ]}>
              <Text
                style={
                  answer
                    ? NiiPdfStyles.preScreeningYesText
                    : NiiPdfStyles.preScreeningNoText
                }>
                {answer ? 'Yes' : 'No'}
              </Text>
            </View>
            <Text style={NiiPdfStyles.preScreeningQuestion}>
              {sanitizeForPdf(question)}
            </Text>
          </View>
        ))}
      </View>
    ))}
  </View>
)

const PdfFormSectionBlocks: React.FC<{
  sections: ReadonlyArray<NiiFormSectionBlock>
}> = ({ sections }) => (
  <View>
    {sections.map((section, index) => (
      <View key={index} style={index > 0 ? NiiPdfStyles.blockGap : undefined}>
        <SubsectionTitle>{section.title}</SubsectionTitle>
        <PdfFieldRows rows={section.rows} />
      </View>
    ))}
  </View>
)

const PdfArrayTables: React.FC<{
  tables: NonNullable<NiiReportSection['arrayTables']>
}> = ({ tables }) => (
  <View>
    {tables.map((block, index) => (
      <View key={index} style={index > 0 ? NiiPdfStyles.blockGap : undefined}>
        <SubsectionTitle>{block.title}</SubsectionTitle>
        {block.entries.length === 0 ? (
          <Text style={NiiPdfStyles.emptyStateText}>No entries.</Text>
        ) : (
          block.entries.map((entry, entryIndex) => (
            <View
              key={entryIndex}
              style={
                entryIndex > 0 ? NiiPdfStyles.arrayEntryDivider : undefined
              }
              wrap>
              <PdfFieldRows rows={entry.cells} />
            </View>
          ))
        )}
      </View>
    ))}
  </View>
)

const ZoneInventoryBlocks: React.FC<{
  zones: ReadonlyArray<NiiZoneInventoryEntry>
}> = ({ zones }) => (
  <View>
    {zones.length === 0 ? (
      <Text style={NiiPdfStyles.emptyStateText}>No zones defined.</Text>
    ) : (
      zones.map((zone) => (
        <View key={zone.zoneIndex} style={NiiPdfStyles.zoneAnalysisBlock}>
          <ZoneHeading color={zone.color}>
            Zone {zone.zoneIndex + 1}: {sanitizeForPdf(zone.title)}
          </ZoneHeading>
          {zone.description && (
            <PdfFieldRow label='Description'>
              <Text style={PdfStyles.preformattedText}>
                {sanitizeForPdf(zone.description)}
              </Text>
            </PdfFieldRow>
          )}
          {zone.designAndFunction && (
            <View>
              <SubsectionTitle>Design and Function</SubsectionTitle>
              <PdfTable table={zone.designAndFunction} />
            </View>
          )}
          {zone.damageMechanisms && (
            <View>
              <SubsectionTitle>Damage Mechanisms</SubsectionTitle>
              <PdfTable table={zone.damageMechanisms} />
            </View>
          )}
        </View>
      ))
    )}
  </View>
)

const ZoneDetailBlock: React.FC<{ zone: NiiZoneDetail }> = ({ zone }) => (
  <View style={NiiPdfStyles.zoneAnalysisBlock}>
    <ZoneHeading color={zone.color}>
      Zone {zone.zoneIndex + 1}: {sanitizeForPdf(zone.title)}
    </ZoneHeading>

    {zone.damageMechanisms && (
      <View>
        <SubsectionTitle>Damage Mechanisms</SubsectionTitle>
        <PdfTable table={zone.damageMechanisms} />
      </View>
    )}

    {zone.designAndFunction && (
      <View>
        <SubsectionTitle>Design and Function</SubsectionTitle>
        <PdfTable table={zone.designAndFunction} />
      </View>
    )}

    {zone.strategy && (
      <View>
        <SubsectionTitle>NII Strategy Selection</SubsectionTitle>
        <PdfTable table={zone.strategy.table} />
        {zone.strategy.overallDecision && (
          <Text style={NiiPdfStyles.strategyDecision} wrap={false}>
            Overall Strategy Decision:{' '}
            {sanitizeForPdf(zone.strategy.overallDecision)}
          </Text>
        )}
      </View>
    )}

    {zone.rba && (
      <View>
        <SubsectionTitle>RBI Additional Information</SubsectionTitle>
        <PdfTable table={zone.rba} />
      </View>
    )}

    {zone.coverageRequirements && (
      <View>
        <SubsectionTitle>Coverage Requirements</SubsectionTitle>
        <PdfTable table={zone.coverageRequirements} />
      </View>
    )}

    {zone.coverageBreakdown && (
      <View wrap={false}>
        <SubsectionTitle>{zone.coverageBreakdown.title}</SubsectionTitle>
        <Text style={NiiPdfStyles.coverageResultPercent}>
          {sanitizeForPdf(zone.coverageBreakdown.resultPercent)}
        </Text>
        <Text style={NiiPdfStyles.coverageResultSubtext}>
          {sanitizeForPdf(zone.coverageBreakdown.resultSubtext)}
        </Text>
        {zone.coverageBreakdown.formulaLines.length > 0 && (
          <View style={NiiPdfStyles.coverageFormulaBlock}>
            {zone.coverageBreakdown.formulaLines.map((line, i) => (
              <Text key={i} style={NiiPdfStyles.coverageFormulaLine}>
                {sanitizeForPdf(line)}
              </Text>
            ))}
          </View>
        )}
        {zone.coverageBreakdown.modifiers.length > 0 && (
          <View style={NiiPdfStyles.coverageModifiers}>
            {zone.coverageBreakdown.modifiers.map((modifier, i) => (
              <View key={i} style={NiiPdfStyles.coverageModifierTag}>
                <Text style={NiiPdfStyles.coverageModifierText}>
                  {sanitizeForPdf(modifier)}
                </Text>
              </View>
            ))}
          </View>
        )}
      </View>
    )}

    {zone.overrides && (
      <View style={NiiPdfStyles.zoneOverrides}>
        <SubsectionTitle>Overrides</SubsectionTitle>
        <PdfTable table={buildZoneOverridesTable(zone.overrides)} />
      </View>
    )}
  </View>
)

const StepSectionContent: React.FC<{ step: NiiReportSection }> = ({ step }) => (
  <View>
    {step.questionLists && step.questionLists.length > 0 && (
      <QuestionLists lists={step.questionLists} />
    )}

    {step.formBlocks && step.formBlocks.length > 0 && (
      <PdfFormSectionBlocks sections={step.formBlocks} />
    )}

    {step.arrayTables && step.arrayTables.length > 0 && (
      <PdfArrayTables tables={step.arrayTables} />
    )}

    {step.zoneInventory && <ZoneInventoryBlocks zones={step.zoneInventory} />}

    {step.zoneImages && step.zoneImages.length > 0 && (
      <View>
        <SubsectionTitle>Zone Reference Images</SubsectionTitle>
        {step.zoneImages.map((img, i) => (
          <View key={i} style={NiiPdfStyles.zoneImageBlock} wrap={false}>
            <Text style={NiiPdfStyles.zoneImageLabel}>{img.name}</Text>
            <Image src={img.dataUrl} style={NiiPdfStyles.zoneImage} />
          </View>
        ))}
      </View>
    )}

    {step.zoneAnalysis && (
      <View>
        {step.zoneAnalysis.zoneBreakdownJustification && (
          <View>
            <SubsectionTitle>Zone Breakdown Justification</SubsectionTitle>
            <Text style={PdfStyles.preformattedText}>
              {sanitizeForPdf(step.zoneAnalysis.zoneBreakdownJustification)}
            </Text>
          </View>
        )}

        {step.zoneAnalysis.overview && (
          <View>
            <SubsectionTitle>Zone Overview Results</SubsectionTitle>
            <PdfTable table={step.zoneAnalysis.overview} />
          </View>
        )}

        {step.zoneAnalysis.zones.map((zone) => (
          <ZoneDetailBlock key={zone.zoneIndex} zone={zone} />
        ))}
      </View>
    )}

    {step.reportCloseout && (
      <View>
        {step.reportCloseout.executiveSummary && (
          <View>
            <SubsectionTitle>Executive Summary</SubsectionTitle>
            <Text style={PdfStyles.preformattedText}>
              {sanitizeForPdf(step.reportCloseout.executiveSummary)}
            </Text>
          </View>
        )}
        {step.reportCloseout.internalComments && (
          <View>
            <SubsectionTitle>Internal Assessment Comments</SubsectionTitle>
            <Text style={PdfStyles.preformattedText}>
              {sanitizeForPdf(step.reportCloseout.internalComments)}
            </Text>
          </View>
        )}
        <SubsectionTitle>Conclusion</SubsectionTitle>
        <PdfFieldRow label='Eligibility'>
          <EligibilityTag
            status={
              step.reportCloseout.eligibility ?? ELIGIBILITY_STATUS.UNCLEAR
            }
          />
        </PdfFieldRow>
        {step.reportCloseout.screenedOutAtPreScreening && (
          <PdfFieldRow label='Pre-screening'>
            <Text style={PdfStyles.preformattedText}>
              Screened out at pre-screening
            </Text>
          </PdfFieldRow>
        )}
        {step.reportCloseout.justification && (
          <PdfFieldRow label='Justification'>
            <Text style={PdfStyles.preformattedText}>
              {sanitizeForPdf(step.reportCloseout.justification)}
            </Text>
          </PdfFieldRow>
        )}
        {step.reportCloseout.recommendations.length > 0 && (
          <PdfFieldRow label='Recommendations'>
            <RecommendationsList items={step.reportCloseout.recommendations} />
          </PdfFieldRow>
        )}
        {step.reportCloseout.zoneRecommendations.length > 0 && (
          <View>
            <SubsectionTitle>Zone-Specific Recommendations</SubsectionTitle>
            {step.reportCloseout.zoneRecommendations.map((zone, i) => (
              <View key={i} style={NiiPdfStyles.zoneBlock}>
                <ZoneHeading color={zone.color}>
                  {sanitizeForPdf(zone.title)}
                </ZoneHeading>
                <RecommendationsList items={zone.zone_recommendations} />
              </View>
            ))}
          </View>
        )}
      </View>
    )}

    {step.signoff && <PdfTable table={buildNiiSignoffTable(step.signoff)} />}

    {step.approvedBeforeSignoffRequired && (
      <Text style={NiiPdfStyles.signoffNote}>
        {NII_APPROVED_BEFORE_SIGNOFF_NOTE}
      </Text>
    )}

    {step.signoff && step.reviewMilestones && (
      <View style={NiiPdfStyles.tableDivider} />
    )}

    {step.reviewMilestones && (
      <View style={step.signoff ? NiiPdfStyles.blockGap : undefined}>
        <PdfTable table={buildNiiReviewMilestoneTable(step.reviewMilestones)} />
      </View>
    )}

    {(!step.formBlocks || step.formBlocks.length === 0) &&
      (!step.arrayTables || step.arrayTables.length === 0) &&
      (!step.questionLists || step.questionLists.length === 0) &&
      step.zoneInventory === undefined &&
      !step.zoneAnalysis &&
      !step.reportCloseout &&
      !step.signoff &&
      !step.reviewMilestones &&
      (!step.zoneImages || step.zoneImages.length === 0) && (
        <Text style={NiiPdfStyles.emptyStateText}>
          No exportable fields for this step.
        </Text>
      )}
  </View>
)

export const NiiReportPdfDocument: React.FC<NiiReportPdfProps> = ({
  chrome,
  steps,
}) => (
  <Document>
    <ReportPage
      header={<PageHeader title='NII Report' />}
      footer={<PageFooter revisionNumber={chrome.analysisVersion} />}>
      <View style={NiiPdfStyles.validityBanner} wrap={false}>
        <Text style={NiiPdfStyles.validityText}>
          {NII_REPORT_VALIDITY_NOTICE}
        </Text>
      </View>
      <PdfSection title='Basic Information' keepTogether>
        {chrome.siteName && (
          <PdfFieldRow label='Site'>
            <Text style={PdfStyles.preformattedText}>{chrome.siteName}</Text>
          </PdfFieldRow>
        )}
        {chrome.assetName && (
          <PdfFieldRow label='Asset'>
            <Text style={PdfStyles.preformattedText}>{chrome.assetName}</Text>
          </PdfFieldRow>
        )}
        <PdfFieldRow label='Analysis Date'>
          <Text style={PdfStyles.preformattedText}>
            {chrome.analysisDate ?? '—'}
          </Text>
        </PdfFieldRow>
        <PdfFieldRow label='Analysis Version'>
          <Text style={PdfStyles.preformattedText}>
            v{chrome.analysisVersion}
          </Text>
        </PdfFieldRow>
      </PdfSection>

      {steps.map((step) => (
        <PdfSection
          key={step.stepId}
          title={step.title}
          keepTogether={niiSectionFitsOnOnePage(step)}>
          <StepSectionContent step={step} />
        </PdfSection>
      ))}
    </ReportPage>
  </Document>
)
